import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faTimes,
    faCrown,
    faUsers,
    faFire,
    faLock,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

interface EventFiltersProps {
    searchTerm: string;
    filterStatus: 'all' | 'active' | 'closed';
    filterRole: 'all' | 'owner' | 'joined';
    onSearchChange: (value: string) => void;
    onFilterChange: (value: 'all' | 'active' | 'closed') => void;
    onFilterRoleChange: (value: 'all' | 'owner' | 'joined') => void;
    /** Off for guests, whose list has no events of their own to tell apart. */
    showRoleFilter?: boolean;
    /** Handed down so the bottom bar's Search action can put the caret here. */
    searchInputRef?: React.RefObject<HTMLInputElement>;
}

const EventFilters: React.FC<EventFiltersProps> = ({
                                                       searchTerm,
                                                       filterStatus,
                                                       filterRole,
                                                       onSearchChange,
                                                       onFilterChange,
                                                       onFilterRoleChange,
                                                       showRoleFilter = true,
                                                       searchInputRef,
                                                   }) => {
    const { t } = useTranslation();

    const roleOptions = [
        { value: 'all' as const, label: t('myEvents.filters.roleAll') },
        { value: 'owner' as const, label: t('myEvents.filters.roleOwner'), icon: faCrown },
        { value: 'joined' as const, label: t('myEvents.filters.roleJoined'), icon: faUsers },
    ];

    const statusOptions = [
        { value: 'all' as const, label: t('myEvents.filters.all') },
        { value: 'active' as const, label: t('myEvents.filters.active'), icon: faFire },
        { value: 'closed' as const, label: t('myEvents.filters.closed'), icon: faLock },
    ];


    return (
        <div className="mb-5 space-y-3">
            {/* Row 1: Search */}
            <div className="relative">
                <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 text-sm"
                />
                <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder={t('myEvents.filters.search')}
                    className="w-full pl-10 pr-10 py-2.5 rounded-xl text-sm bg-white/60 dark:bg-gray-800/60 border border-gray-200/60 dark:border-gray-700/60 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400 dark:focus:border-indigo-500 outline-none transition-all backdrop-blur-sm"
                />
                {searchTerm && (
                    <button
                        onClick={() => onSearchChange('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                        <FontAwesomeIcon icon={faTimes} className="text-sm" />
                    </button>
                )}
            </div>

            {/* Row 2: Filter chips + Sort — all inline */}
            <div className="flex items-center gap-2 flex-wrap">
                {showRoleFilter && (
                    <>
                        {/* Role filter — the app's segmented control at filter weight */}
                        <div className="glass-tab-bar inline-flex gap-0.5 !p-0.5">
                            {roleOptions.map((opt) => (
                                <button
                                    key={opt.value}
                                    onClick={() => onFilterRoleChange(opt.value)}
                                    className={`glass-tab-sm flex items-center gap-1.5 ${
                                        filterRole === opt.value ? 'glass-tab-active-soft' : ''
                                    }`}
                                >
                                    {opt.icon && (
                                        <FontAwesomeIcon icon={opt.icon} className="text-[10px]" />
                                    )}
                                    {opt.label}
                                </button>
                            ))}
                        </div>

                        {/* Separator dot */}
                        <span className="text-gray-300 dark:text-gray-600 text-xs select-none hidden sm:inline">·</span>
                    </>
                )}

                {/* Status filter — same control, same weight */}
                <div className="glass-tab-bar inline-flex gap-0.5 !p-0.5">
                    {statusOptions.map((opt) => (
                        <button
                            key={opt.value}
                            onClick={() => onFilterChange(opt.value)}
                            className={`glass-tab-sm flex items-center gap-1.5 ${
                                filterStatus === opt.value ? 'glass-tab-active-soft' : ''
                            }`}
                        >
                            {opt.icon && (
                                <FontAwesomeIcon icon={opt.icon} className={`text-[10px] ${
                                    opt.value === 'active' && filterStatus === opt.value ? 'text-orange-500' : 
                                    opt.value === 'closed' && filterStatus === opt.value ? 'text-gray-500' : ''
                                }`} />
                            )}
                            {opt.label}
                        </button>
                    ))}
                </div>

            </div>
        </div>
    );
};

export default EventFilters;